/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team
 * <mailto:radmanesh@gmail.com Arman Radmanesh>
 * <mailto:salarirad@gmail.com Mostafa Salari Rad>
 */
import { Timestamp, addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { db } from '../../firebase';
import Breadcrumbs from '../components/Breadcrumbs';
import './admin.css';

const STEPS = [
  'Name your project',
  'Invitation window',
  'Review & create',
];

const NewProject = () => {
  const { currentUser, loading } = useAuthContext();
  const [activeStep, setActiveStep] = React.useState(0);
  const [projectName, setProjectName] = React.useState('');
  const [inviteFrom, setInviteFrom] = React.useState('');
  const [inviteTo, setInviteTo] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  const navigate = useNavigate();
  const { addAlert } = useAlert();

  const handleNext = () => setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleCreateProject = async () => {
    setCreating(true);
    // Email sign-ups are accepted within this window (default: from today for one year).
    const now = new Date();
    const invitationWindow = {
      from: Timestamp.fromDate(inviteFrom ? new Date(`${inviteFrom}T00:00:00`) : new Date(now.getFullYear(), now.getMonth(), now.getDate())),
      to: Timestamp.fromDate(inviteTo ? new Date(`${inviteTo}T23:59:59`) : new Date(now.getFullYear() + 1, now.getMonth(), now.getDate(), 23, 59, 59)),
    };
    const projectDoc = {
      name: projectName,
      devices: [],
      sensors: [],
      settings: {
        detailLevel: '1min',
        dateRange: {
          from: Timestamp.fromMillis(new Date(2021, 1, 29).getTime()),
          to: Timestamp.fromMillis(new Date().getTime()),
        },
        enabled: true,
        invitationWindow,
      },
      downloadSettings: [],
      invitationWindow, // older readers; registration reads settings.invitationWindow
      cronSettings: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: currentUser?.uid,
    };

    addDoc(collection(db, 'projects'), projectDoc)
      .then((docRef) => {
        const projectId = docRef.id;
        updateDoc(doc(db, 'projects', projectId), { id: projectId })
          .then(() => {
            addAlert('success', `Project "${projectName}" created!`);
            navigate(`/admin/showProject/${projectId}`);
          })
          .catch((err) => {
            console.error('Error updating project:', err);
            addAlert('error', `Project could not be updated after creation.`);
          });
      })
      .catch((err) => {
        console.error('Error creating project:', err);
        addAlert('error', 'Project could not be created.');
        setCreating(false);
      });
  };

  if (loading) {
    return (
      <div className="ja-root">
        <div className="ja-page">
          <div className="ja-progress"><div className="ja-progress-bar" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="ja-root">
      <div className="ja-page" style={{ maxWidth: 680 }}>

        {/* Header */}
        <div className="ja-page-header">
          <div>
            <Breadcrumbs items={[{ label: 'Dashboard', to: '/admin' }, { label: 'New project' }]} />
            <h1 className="ja-page-title"><strong>New project</strong></h1>
            <p className="ja-page-subtitle">Set up a wearable data collection project.</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="ja-stepper">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className="ja-step-item">
                <div className={`ja-step-circle${i === activeStep ? ' active' : i < activeStep ? ' done' : ''}`}>
                  {i < activeStep ? '✓' : i + 1}
                </div>
                <span className={`ja-step-label${i === activeStep ? ' active' : ''}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`ja-step-connector${i < activeStep ? ' done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0 — name */}
        {activeStep === 0 && (
          <div className="ja-card">
            <div className="ja-card-header">
              <h2 className="ja-card-title">Project name</h2>
            </div>
            <p style={{ fontSize: 13, color: 'var(--jp-gray-600)', marginBottom: 18 }}>
              Choose a clear, descriptive name for your study or project.
            </p>
            <div className="ja-field">
              <label htmlFor="projectName">Project name <span style={{ color: '#C0392B' }}>*</span></label>
              <input
                id="projectName"
                className="ja-input"
                placeholder="e.g. Sleep &amp; Activity Study 2025"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Step 1 — invitation window */}
        {activeStep === 1 && (
          <div className="ja-card">
            <div className="ja-card-header">
              <h2 className="ja-card-title">Invitation window</h2>
              <span className="ja-chip ja-chip-inactive">optional</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--jp-gray-600)', marginBottom: 18 }}>
              Set the date range during which participants may register.
            </p>
            <div className="ja-field">
              <label htmlFor="inviteFrom">From</label>
              <input
                id="inviteFrom"
                type="date"
                className="ja-input"
                value={inviteFrom}
                onChange={(e) => setInviteFrom(e.target.value)}
              />
            </div>
            <div className="ja-field">
              <label htmlFor="inviteTo">To</label>
              <input
                id="inviteTo"
                type="date"
                className="ja-input"
                value={inviteTo}
                onChange={(e) => setInviteTo(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Step 2 — review */}
        {activeStep === 2 && (
          <div className="ja-card">
            <div className="ja-card-header">
              <h2 className="ja-card-title">Review</h2>
            </div>
            <p style={{ fontSize: 13, color: 'var(--jp-gray-600)', marginBottom: 18 }}>
              Confirm your project details before creating.
            </p>
            <div style={{ background: 'var(--jp-gray-50)', border: '1px solid var(--jp-gray-200)', borderRadius: 'var(--jp-radius-sm)', padding: '14px 18px', marginBottom: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <span className="ja-label" style={{ marginBottom: 2 }}>Project name</span>
                  <span style={{ fontWeight: 500, fontSize: 15 }}>{projectName || <em style={{ color: 'var(--jp-gray-400)' }}>not set</em>}</span>
                </div>
                {(inviteFrom || inviteTo) && (
                  <div>
                    <span className="ja-label" style={{ marginBottom: 2 }}>Invitation window</span>
                    <span style={{ fontFamily: 'var(--jp-mono)', fontSize: 13 }}>
                      {inviteFrom || '—'} → {inviteTo || '—'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="ja-btn-row" style={{ marginTop: 20, justifyContent: 'space-between' }}>
          <button
            className="ja-btn ja-btn-ghost"
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            ← Back
          </button>
          <div className="ja-btn-row">
            {activeStep < STEPS.length - 1 ? (
              <button
                className="ja-btn ja-btn-primary"
                disabled={activeStep === 0 && !projectName.trim()}
                onClick={handleNext}
              >
                Next →
              </button>
            ) : (
              <button
                className="ja-btn ja-btn-primary"
                disabled={!projectName.trim() || creating}
                onClick={handleCreateProject}
              >
                {creating ? 'Creating…' : 'Create project'}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default NewProject;
